import { applyTransaction, combineQueries, filterNilValue } from '@datorama/akita';
import { formatISO, parseISO } from 'date-fns';
import { isEqual, omit } from 'lodash-es';
import { combineLatest, distinctUntilChanged, map, type Subscription } from 'rxjs';
import { entities$, query } from './query';
import store, { type KPISetState } from './store';
import { activeOrganisationId$ } from 'context/OrganisationContext/query';
import { context$ as periodContext$ } from 'context/PeriodContext/query';
import { includeSubsidiaries$, language$ } from 'context/UserContext/query';
import CRUDService, { type QueryDependency, type QueryResolvedValues } from 'state/CRUDService';
import FeaturesService from 'state/Feature/service';
import kpiStore from 'state/KPI/store';
import kpiAreaStore from 'state/KPIArea/store';
import kpiContentStore from 'state/KPIContent/store';
import kpiFieldStore from 'state/KPIField/store';
import KPIScopeService from 'state/KPIScope/service';
import kpiSubStore from 'state/KPISub/store';
import { query as uiQuery } from 'state/UI/query';
import type GlobalUI from 'state/UI/types';
import { activeEntity$ as activeUIPeriod$ } from 'state/UIPeriod/query';
import {
   type KPI,
   type KpiArea,
   type KpiContent,
   type KpiField,
   type KpiSub,
   type Organisation,
   type Period,
   type ReportingStandard,
   type Tag,
   type UIPeriod,
} from 'types/models';

let KPIScopeServiceInstance: KPIScopeService | null = null;

export default class KPISetService extends CRUDService<KPISetState> {
   private static instance: KPISetService;
   private readonly featuresService!: FeaturesService;
   statisticsQueryParamsObservable;
   statisticsSubscription?: Subscription;
   statisticsController?: AbortController;
   exportController?: AbortController;

   constructor() {
      if (!KPISetService.instance) {
         super('kpiset', store, query, {
            useOrganisationContext: true,
            usePeriodContext: true,
            includeSubsidiaries: false,
         });

         this.statisticsQueryParamsObservable = this.getStatisticsQueryParams();
         this.exportController = new AbortController();
         this.featuresService = new FeaturesService();

         entities$.subscribe((kpiSet) => {
            if (this.getLoading() === false) {
               kpiAreaStore.setLoading(true);
               kpiStore.setLoading(true);
               kpiSubStore.setLoading(true);
               kpiContentStore.setLoading(true);
               kpiFieldStore.setLoading(true);

               const kpiFieldIds = kpiSet.flatMap((kpiArea) =>
                  (kpiArea?.kpis ?? []).flatMap((kpi: KPI) =>
                     kpi.kpiSubs.flatMap((kpiSub: KpiSub) =>
                        kpiSub.kpiContents.flatMap((kpiContent: KpiContent) => kpiContent.kpiFields.map((kpiField: KpiField) => kpiField.id))
                     )
                  )
               );

               const kpiContentIds = kpiSet.flatMap((kpiArea) =>
                  (kpiArea?.kpis ?? []).flatMap((kpi: KPI) =>
                     kpi.kpiSubs.flatMap((kpiSub: KpiSub) => kpiSub.kpiContents.map((kpiContent: KpiContent) => kpiContent.id))
                  )
               );

               const kpiSubIds = kpiSet.flatMap((kpiArea) =>
                  (kpiArea?.kpis ?? []).flatMap((kpi: KPI) => kpi.kpiSubs.flatMap((kpiSub: KpiSub) => kpiSub.id))
               );

               const kpiIds = kpiSet.flatMap((kpiArea) => (kpiArea?.kpis ?? []).flatMap((kpi: KPI) => kpi.id));

               const kpiAreaIds = kpiSet.map((kpiArea) => kpiArea.id);

               return applyTransaction(() => {
                  kpiFieldStore.remove(({ id }: KpiField) => !kpiFieldIds.includes(id));
                  kpiContentStore.remove(({ id }: KpiContent) => !kpiContentIds.includes(id));
                  kpiSubStore.remove(({ id }: KpiSub) => !kpiSubIds.includes(id));
                  kpiStore.remove(({ id }: KPI) => !kpiIds.includes(id));
                  kpiAreaStore.remove(({ id }: KpiArea) => !kpiAreaIds.includes(id));

                  kpiAreaStore.upsertMany(kpiSet.map((kpiArea) => omit(kpiArea, ['kpis'])) as KpiArea[]);

                  kpiSet.forEach((kpiArea) => {
                     if (Array.isArray(kpiArea?.kpis)) {
                        kpiStore.upsertMany(
                           kpiArea.kpis.map(
                              (kpi) =>
                                 omit({ ...kpi, kpiArea: { ...kpi.kpiArea, id: kpiArea.id, slug: kpiArea.slug } }, ['kpiSubs']) as unknown as KPI
                           )
                        );

                        kpiArea.kpis.forEach((kpi: KPI) => {
                           if (Array.isArray(kpi?.kpiSubs)) {
                              kpiSubStore.upsertMany(
                                 kpi.kpiSubs.map((kpiSub: KpiSub) =>
                                    omit(
                                       {
                                          ...kpiSub,
                                          kpi: {
                                             id: kpi.id,
                                             slug: kpi.slug,
                                             position: kpi.position,
                                             kpiArea: { id: kpiArea.id, slug: kpiArea.slug, position: kpiArea.position },
                                          },
                                       },
                                       ['kpiContents']
                                    )
                                 )
                              );

                              kpi.kpiSubs.forEach((kpiSub: KpiSub) => {
                                 if (Array.isArray(kpiSub?.kpiContents)) {
                                    kpiContentStore.upsertMany(
                                       kpiSub.kpiContents.map(
                                          (kpiContent) =>
                                             omit({ ...kpiContent, kpiSub: { id: kpiSub.id, slug: kpiSub.slug } }, ['kpiFields']) as KpiContent
                                       )
                                    );

                                    kpiSub.kpiContents.forEach((kpiContent) => {
                                       if (Array.isArray(kpiContent?.kpiFields)) {
                                          kpiFieldStore.upsertMany(
                                             kpiContent.kpiFields.map((kpiField: KpiField) => ({
                                                ...(omit(kpiField, ['rows', 'formulaUnit', 'errors']) as KpiField),
                                                ...(kpiField?.formulaUnit ? { formulaUnit: kpiField?.formulaUnit } : {}),
                                                kpiContent: {
                                                   id: kpiContent.id,
                                                   slug: kpiContent.slug,
                                                   position: kpiContent.position,
                                                   key: kpiContent.key,
                                                   kpiSub: {
                                                      id: kpiSub.id,
                                                      slug: kpiSub.slug,
                                                      kpi: {
                                                         id: kpi.id,
                                                         slug: kpi.slug,
                                                         kpiArea: {
                                                            id: kpiArea.id,
                                                            slug: kpiArea.slug,
                                                         },
                                                      },
                                                   },
                                                } as unknown as KpiContent,
                                             }))
                                          );

                                          kpiContent.kpiFields
                                             .filter(({ rows }: KpiField) => rows?.length > 0)
                                             .forEach((kpiField: KpiField) =>
                                                kpiFieldStore.update(kpiField.id, (entity: KpiField) => ({
                                                   ...entity,
                                                   rows: [
                                                      ...(entity?.rows ?? []),
                                                      ...kpiField.rows.filter(({ id }) => !(entity?.rows ?? []).map(({ id }) => id).includes(id)),
                                                   ],
                                                   kpiContent: {
                                                      id: kpiContent.id,
                                                      slug: kpiContent.slug,
                                                      key: kpiContent.key,
                                                      kpiSub: {
                                                         id: kpiSub.id,
                                                         slug: kpiSub.slug,
                                                         kpi: {
                                                            id: kpi.id,
                                                            slug: kpi.slug,
                                                            kpiArea: {
                                                               id: kpiArea.id,
                                                               slug: kpiArea.slug,
                                                            },
                                                         },
                                                      },
                                                   },
                                                }))
                                             );
                                       }
                                    });
                                 }
                              });
                           }
                        });
                     }
                  });

                  kpiAreaStore.setLoading(false);
                  kpiStore.setLoading(false);
                  kpiSubStore.setLoading(false);
                  kpiContentStore.setLoading(false);
                  kpiFieldStore.setLoading(false);
               });
            }
            return undefined;
         });

         KPISetService.instance = this;
      }

      // Service shall be instantiated only once, because otherwise the observable will be created for each service instance

      return KPISetService.instance;
   }

   getQueryParams() {
      const dependencies: QueryDependency[] = [];

      if (this.useOrganisationContext) {
         dependencies.push({ observable: activeOrganisationId$.pipe(filterNilValue()), key: 'organisationId' });
      }
      if (this.usePeriodContext) {
         dependencies.push({ observable: periodContext$.pipe(filterNilValue()), key: 'period' });
      }
      dependencies.push({ observable: language$.pipe(filterNilValue()), key: 'language' });

      return combineLatest(dependencies.map((d) => d.observable)).pipe(
         distinctUntilChanged(isEqual),
         map((values) => {
            const resolvedValues: {
               organisationId: Organisation['id'];
               period: Period;
               language: boolean;
            } = values.reduce((acc: QueryResolvedValues, value: any, index: number) => {
               acc[dependencies[index].key] = value;
               return acc;
            }, {});
            const { organisationId, period } = resolvedValues;

            const urlParams = new URLSearchParams(this.customQueryParams);

            urlParams.set('scope', 'any');

            if (organisationId) {
               urlParams.set('organisationId', String(organisationId));
            }

            if (period) {
               urlParams.set('from', period.from);
               urlParams.set('to', period.to);
            }

            if (
               ((this.useOrganisationContext && organisationId) || !this.useOrganisationContext) &&
               ((this.usePeriodContext && period) || !this.usePeriodContext)
            ) {
               return `?${urlParams.toString()}`;
            }
         })
      );
   }

   async import(file: File) {
      const formData = new FormData();
      formData.append('file', file);

      try {
         const result = await this.httpClient.post(`/${this.version}/${this.entityName}/import/json`, formData, {
            headers: {
               'Content-Type': 'multipart/form-data',
            },
         });

         return result.data;
      } catch (err: any) {
         throw new Error(err?.response?.data?.message ?? err?.message ?? err);
      }
   }

   async export(reportingStandardId: number, kpiAreas: KpiArea['id'][] = []) {
      const searchParams = new URLSearchParams();

      if (reportingStandardId) {
         searchParams.set('reportingStandardId', String(reportingStandardId));
      }
      if (kpiAreas?.length > 0) {
         searchParams.set('kpiAreaIds', kpiAreas.join());
      }

      return this.httpClient
         .get(`/${this.version}/${this.entityName}/export/json?${searchParams}`)
         .then((resp) => resp)
         .catch((error) => {
            this.setError(error);
         });
   }

   cancelExport() {
      if (!this.featuresService.isFeatureActive('BackgroundExport') && this.exportController) {
         this.exportController.abort();
      }
   }

   async exportValues(
      reportingStandardIds: number[],
      from: string,
      to: string,
      includeSubsidiaryValues: boolean,
      fullConsolidation: boolean,
      onlyApproved = false,
      reportGroupByOrganisation = false,
      emptyFields = false,
      reportUsePeriodTitle = false,
      tags: number[] = [],
      formatType = 'excel',
      reportType = 'regular',
      keyFigureKpiAreaIds: KpiArea['id'][] = [],
      keyFigureKpiIds: KPI['id'][] = [],
      keyFigureKpiSubIds: KpiSub['id'][] = [],
      organisationIds: Organisation['id'][] | null = null
   ) {
      const searchParams = new URLSearchParams();

      if (this.exportController) {
         this.exportController.abort();
      }

      this.exportController = new AbortController();

      if (Array.isArray(reportingStandardIds) && reportingStandardIds.length > 0) {
         searchParams.set('reportingStandardIds', reportingStandardIds.join(','));
      }

      if (Array.isArray(tags) && tags.length > 0) {
         searchParams.set('tag', tags.join(','));
      }

      if (Array.isArray(keyFigureKpiAreaIds) && keyFigureKpiAreaIds.length > 0) {
         searchParams.set('kpiAreaIds', keyFigureKpiAreaIds.join(','));
      }

      if (Array.isArray(keyFigureKpiIds) && keyFigureKpiIds.length > 0) {
         searchParams.set('kpiIds', keyFigureKpiIds.join(','));
      }

      if (Array.isArray(keyFigureKpiSubIds) && keyFigureKpiSubIds.length > 0) {
         searchParams.set('kpiSubIds', keyFigureKpiSubIds.join(','));
      }

      if (Array.isArray(organisationIds) && organisationIds.length > 0) {
         searchParams.set('organisationId', organisationIds.join(','));
      }

      searchParams.set('from', from);
      searchParams.set('to', to);
      searchParams.set('aggregationLevel', includeSubsidiaryValues && reportGroupByOrganisation ? 'entity' : 'group');
      searchParams.set('includeSubsidiaries', String(includeSubsidiaryValues ?? false));
      searchParams.set('fullConsolidation', String(fullConsolidation ?? false));
      searchParams.set('onlyApproved', String(onlyApproved));
      searchParams.set('emptyFields', String(emptyFields));
      searchParams.set('reportType', reportType);
      searchParams.set('usePeriodTitle', String(reportUsePeriodTitle ?? false));

      // todo: remove this when the backend is ready
      const tmpEntityName = reportType === 'regular' ? this.entityName : 'kpivalues';

      let action = '';

      switch (reportType) {
         case 'regular':
            action = '/export/excel';
            searchParams.set('type', formatType);
            break;
         case 'audit':
         case 'audit-lite':
            action = '/export';
            searchParams.set('type', formatType);
            break;
         case 'mapped':
            searchParams.set('type', 'kpi');
            break;
      }

      return this.httpClient
         .get(`/${this.version}/${tmpEntityName}${action}?${searchParams}`, { responseType: 'blob', signal: this.exportController.signal })
         .then((resp) => resp)
         .catch((error) => {
            this.setError(error);
         });
   }

   getStatisticsQueryParams() {
      const dependencies: QueryDependency[] = [];

      if (this.useOrganisationContext) {
         dependencies.push({ observable: activeOrganisationId$.pipe(filterNilValue()), key: 'organisationId' });
      }

      dependencies.push({ observable: activeUIPeriod$.pipe(filterNilValue()), key: 'uiPeriod' });
      dependencies.push({ observable: includeSubsidiaries$.pipe(filterNilValue()), key: 'includeSubsidiaries' });
      dependencies.push({ observable: uiQuery.select('statisticsFilters').pipe(filterNilValue()), key: 'filters' });

      return combineQueries(dependencies.map((d) => d.observable)).pipe(
         distinctUntilChanged(isEqual),
         map((values) => {
            const resolvedValues: {
               organisationId: Organisation['id'];
               uiPeriod: UIPeriod;
               includeSubsidiaries: boolean;
               filters: GlobalUI['statisticsFilters'];
            } = values.reduce((acc: QueryResolvedValues, value: any, index: number) => {
               acc[dependencies[index].key] = value;
               return acc;
            }, {});

            const { organisationId, uiPeriod, includeSubsidiaries, filters } = resolvedValues;

            const urlParamsSubClass = new URLSearchParams();

            if (this.useScope === true) {
               urlParamsSubClass.set('scope', 'true');
            } else if (this.useScope === false) {
               urlParamsSubClass.set('scope', 'false');
            } else {
               urlParamsSubClass.set('scope', 'any');
            }

            if (organisationId) {
               urlParamsSubClass.set('organisationId', String(organisationId));
            }

            if (uiPeriod) {
               urlParamsSubClass.set('from', formatISO(uiPeriod.from, { representation: 'date' }));
               urlParamsSubClass.set('to', formatISO(uiPeriod.to, { representation: 'date' }));
            }

            if (typeof includeSubsidiaries === 'boolean') {
               urlParamsSubClass.set('includeSubsidiaries', String(includeSubsidiaries));
            }

            if (filters) {
               const groupedFilter = Object.entries(filters).reduce((acc, [kind, filterItems = []]) => {
                  const formatData = (date: string | Date) => formatISO(typeof date === 'string' ? parseISO(date) : date, { representation: 'date' });
                  switch (kind) {
                     case 'periods':
                        return {
                           ...acc,
                           ...[filterItems]
                              .flat()
                              .filter((item) => !!item)
                              .map((period) => ({ from: formatData((period as Period).from), to: formatData((period as Period).to) })),
                        };
                     case 'reportingStandards':
                        return {
                           ...acc,
                           reportingStandardId: filterItems.map((filter) => (filter as ReportingStandard)?.id),
                        };
                     case 'organisations':
                        return {
                           ...acc,
                           organisationId: filterItems.map((filter) => (filter as Organisation)?.id),
                        };
                     case 'tags':
                        return {
                           ...acc,
                           tag: filterItems.map((filter) => (filter as Tag)?.name),
                        };
                     default:
                        return acc;
                  }
               }, {});

               for (const [key, value] of Object.entries(groupedFilter)) {
                  if (Array.isArray(value) && value.length) {
                     urlParamsSubClass.set(key, [value].flat().join(','));
                  }
               }
            }

            if (uiPeriod && organisationId) {
               return `?${urlParamsSubClass.toString()}`;
            }
         })
      );
   }

   async getStatistics() {
      if (this.statisticsSubscription === undefined || this.statisticsSubscription?.closed) {
         this.statisticsSubscription = this.statisticsQueryParamsObservable
            ?.pipe(filterNilValue(), distinctUntilChanged())
            .subscribe((queryString) => {
               if (typeof queryString === 'string') {
                  if (this.statisticsController) {
                     this.statisticsController.abort();
                  }

                  this.statisticsController = new AbortController();

                  this.store.setLoading(true);
                  this.httpClient
                     .get(`/${this.version}/statistics/kpisinfo${queryString}`, {
                        signal: this.statisticsController.signal,
                     })
                     .then((resp) => {
                        this.store.update({ statistics: resp.data });
                        return this.store.setLoading(false);
                     })
                     .catch((error) => {
                        if (!(error?.name === 'AbortError' || error?.code === 'ERR_CANCELED')) {
                           return this.setError(error);
                        }
                        return this.store.setLoading(false);
                     });
               }

               return undefined;
            });
      }
   }

   setActiveNode(type: string | null = null, id: number | null = null) {
      if (!type || !id) {
         this.store.update({ activeNode: undefined });
      } else {
         this.store.update({ activeNode: { type, id } });
      }
   }

   deleteEntity(entityId: number, kind: string) {
      if (kind === 'kpiArea') {
         return this.store.remove(entityId);
      } else if (kind === 'kpi') {
         return this.store.update(null, (kpiArea: KpiArea) => ({
            ...kpiArea,
            kpis: kpiArea.kpis.filter(({ id }) => id !== entityId),
         }));
      } else if (kind === 'kpiSub') {
         return this.store.update(null, (kpiArea: KpiArea) => ({
            ...kpiArea,
            kpis: kpiArea.kpis.map((kpi: KPI) => ({
               ...kpi,
               kpiSubs: kpi.kpiSubs.filter(({ id }: KpiSub) => id !== entityId),
            })),
         }));
      } else if (kind === 'kpiContent') {
         return this.store.update(null, (kpiArea: KpiArea) => ({
            ...kpiArea,
            kpis: kpiArea.kpis.map((kpi: KPI) => ({
               ...kpi,
               kpiSubs: kpi.kpiSubs.map((kpiSub: KpiSub) => ({
                  ...kpiSub,
                  kpiContents: kpiSub.kpiContents.filter(({ id }: KpiContent) => id !== entityId),
               })),
            })),
         }));
      } else if (kind === 'kpiField') {
         return this.store.update(null, (kpiArea: KpiArea) => ({
            ...kpiArea,
            kpis: kpiArea.kpis.map((kpi: KPI) => ({
               ...kpi,
               kpiSubs: kpi.kpiSubs.map((kpiSub: KpiSub) => ({
                  ...kpiSub,
                  kpiContents: kpiSub.kpiContents.map((kpiContent: KpiContent) => ({
                     ...kpiContent,
                     kpiFields: kpiContent.kpiFields.filter(({ id }: KpiField) => id !== entityId),
                  })),
               })),
            })),
         }));
      }
   }

   async createOrUpdateFieldScope(kpiFieldId: KpiField['id'], kpiAreaId: KpiArea['id'], existingScope: any, changes: any) {
      KPIScopeServiceInstance ??= new KPIScopeService();

      return KPIScopeServiceInstance.createOrUpdateScope({
         id: existingScope?.id,
         changes,
         onSuccess: (updatedScopes) => {
            this.store.update(kpiAreaId, ({ kpis }: KpiArea) => ({
               kpis: kpis.map((kpi: KPI) => ({
                  ...kpi,
                  kpiSubs: kpi.kpiSubs.map((kpiSub: KpiSub) => ({
                     ...kpiSub,
                     kpiContents: kpiSub.kpiContents.map((kpiContent: KpiContent) => ({
                        ...kpiContent,
                        kpiFields: kpiContent.kpiFields.map((kpiField: KpiField) => {
                           if (kpiField.id !== kpiFieldId) {
                              return kpiField;
                           }

                           if (existingScope?.id > 0) {
                              return {
                                 ...kpiField,
                                 kpiScopes: [updatedScopes, ...(kpiField?.kpiScopes ?? []).filter((s: any) => s.id !== existingScope.id)],
                              };
                           } else {
                              return {
                                 ...kpiField,
                                 kpiScopes: [...(kpiField?.kpiScopes ?? []), ...updatedScopes],
                              };
                           }
                        }),
                     })),
                  })),
               })),
            }));
         },
      });
   }

   unsubscribeStatistics() {
      if (this.statisticsSubscription && !this.statisticsSubscription.closed) {
         this.statisticsSubscription.unsubscribe();
      }
      if (this.statisticsController) {
         this.statisticsController.abort();
      }
   }
}
